/**
 * Mounts PostHog for the Member Portal. Must render inside both ClerkProvider
 * (it reads Clerk auth via hooks) and BrowserRouter (pageviews use useLocation).
 *
 *  - initializes the client once,
 *  - captures SPA pageviews on route change,
 *  - identifies by the Clerk user id on login and resets on logout,
 *  - emits a once-per-session `user_logged_in` event for the funnel.
 *
 * When VITE_POSTHOG_KEY is unset (e.g. local dev) this is fully inert.
 */
import { ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  initPostHog,
  posthog,
  APP,
  POSTHOG_ENABLED,
  isInternalEmail,
} from "@/lib/analytics/posthog";

if (typeof window !== "undefined") {
  initPostHog();
}

function PageviewTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!POSTHOG_ENABLED) return;
    let url = window.location.origin + pathname;
    if (search) url += search;
    posthog.capture("$pageview", { $current_url: url });
  }, [pathname, search]);
  return null;
}

function IdentifyOnAuth() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { user } = useUser();
  const wasSignedIn = useRef(false);

  useEffect(() => {
    if (!POSTHOG_ENABLED || !isLoaded) return;
    if (isSignedIn && userId) {
      wasSignedIn.current = true;
      const email = user?.primaryEmailAddress?.emailAddress || null;
      posthog.identify(userId, {
        email,
        name: user?.fullName || null,
        app: APP,
        is_internal: isInternalEmail(email),
      });
      // First sign-in of this browser session → emit once (funnel "first login").
      const flag = `ph_login_${userId}`;
      if (!sessionStorage.getItem(flag)) {
        sessionStorage.setItem(flag, "1");
        posthog.capture("user_logged_in", { app: APP });
      }
    } else if (wasSignedIn.current) {
      // Only reset on an actual sign-out, never on anonymous page loads (that would
      // churn the anonymous distinct_id and break anonymous → identified stitching).
      wasSignedIn.current = false;
      posthog.reset();
    }
  }, [isLoaded, isSignedIn, userId, user]);

  return null;
}

export default function PostHogProvider({ children }: { children: ReactNode }) {
  if (!POSTHOG_ENABLED) return <>{children}</>;
  return (
    <>
      <PageviewTracker />
      <IdentifyOnAuth />
      {children}
    </>
  );
}
